import {faVideo} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {swalConfig, toastConfig} from '../../../../config';
import useRouteStore from '../../../../hooks/useRouteStore';
import {generateTitle, generateUrl} from '../../../../features/tools';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import Select from 'react-select';
import Creatable from 'react-select/creatable';
import {getSerie, getGenres, updateSerie, deleteSerie} from '../../../../api';
import Input from '../../../../common/Input';
import RichTextArea from '../../../../common/RichTextArea';
import Episode from './Episode';
import PosterMedia from '../../../../common/PosterMedia';
import BackdropMedia from '../../../../common/BackdropMedia';

const AdminSerie = () => {
    const {id} = useParams();
    const mySwal = withReactContent(Swal);
    const [serie, setSerie] = useState({});
    const [genres, setGenres] = useState([]);
    const [selectedGenres, setSelectedGenres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [episodeClicked, setEpisodeClicked] = useState(null);
    const navigate = useNavigate();
    const setRoute = useRouteStore((state) => state.setRoute);

    // Upload Media

    const stateOptions = [
        {value: 'En emisión en Turquía', label: 'En emisión en Turquía'},
        {value: 'Finalizada en Turquía', label: 'Finalizada en Turquía'},
        {value: 'Finalizada, pero en traducción', label: 'Finalizada, pero en traducción'},
    ];

    useEffect(() => {
        setRoute([
            {name: 'Inicio', url: '/administracion'},
            {name: 'Series', url: '/administracion/series'},
            {name: serie.titulo_original || 'Serie', url: '/administracion/series/' + id},
        ]);
    }, [serie]);

    useEffect(() => {
        getSerie(id).then((res) => {
            setSerie(res.data);
            setLoading(false);
        });

        getGenres().then((res) => {
            setGenres(res.data.map((genre) => ({value: genre.name, label: genre.name})));
        });
    }, []);

    useEffect(() => {
        if (serie?.generos && genres.length > 0) {
            setSelectedGenres(serie.generos.map((genre) => ({value: genre, label: genre})));
        }
    }, [genres, serie]);

    const saveSerie = async () => {
        setSaving(true);
        try {
            await updateSerie(id, {...serie, generos: selectedGenres.map((genre) => genre.value)});
            toast.success('Serie actualizada', toastConfig);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al guardar la serie', toastConfig);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteSerie = async () => {
        const res = await mySwal.fire({...swalConfig.confirm, text: `Estás seguro de borrar "${serie.titulo_original}"`});

        if (res.isConfirmed) {
            setDeleting(true);
            try {
                await deleteSerie(id);
                toast.success('Serie eliminada', toastConfig);
                navigate('/administracion/series');
            } catch (err) {
                toast.error(err.response?.data?.message || 'Error al eliminar la serie', toastConfig);
                setDeleting(false);
            }
        }
    };

    const refresh = () => {
        setLoading(true);
        getSerie(id)
            .then((res) => {
                setSerie(res.data);
                setLoading(false);
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Error al cargar la serie', toastConfig);
                setLoading(false);
            });
    };

    return (
        <div className='font-montserrat w-full max-h-full'>
            {!loading ? (
                <>
                    <div className='flex items-center justify-between gap-3 pb-1 mb-2 border-b-2'>
                        <h1 className='font-semibold text-xl min-w-0 truncate'>Serie: {serie.titulo_original}</h1>
                        <div className='flex gap-2 shrink-0'>
                            <button
                                onClick={saveSerie}
                                disabled={saving || deleting}
                                className='flex items-center gap-1.5 bg-red-1 text-white font-semibold text-sm px-4 py-1.5 rounded hover:text-red-1 hover:bg-opacity-5 disabled:opacity-50 disabled:cursor-not-allowed transition-all'
                            >
                                {saving && (
                                    <svg className='animate-spin h-3.5 w-3.5' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                                        <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                                    </svg>
                                )}
                                Guardar
                            </button>
                            <button
                                onClick={handleDeleteSerie}
                                disabled={saving || deleting}
                                className='flex items-center gap-1.5 bg-red-1 text-white font-semibold text-sm px-4 py-1.5 rounded hover:text-red-1 hover:bg-opacity-5 disabled:opacity-50 disabled:cursor-not-allowed transition-all'
                            >
                                {deleting && (
                                    <svg className='animate-spin h-3.5 w-3.5' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                                        <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                                    </svg>
                                )}
                                Borrar
                            </button>
                        </div>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4'>
                        <Input
                            id='titulo_original'
                            value={serie.titulo_original}
                            onChange={(e) =>
                                setSerie({...serie, titulo_original: e.target.value, titulo: generateTitle(e.target.value), url: generateUrl(e.target.value)})
                            }
                            label='Título original'
                            labelClassName='w-36'
                        />
                        <div className='flex items-center gap-2 font-montserrat text-lg'>
                            <label htmlFor='genres' className='font-semibold text-red-1 whitespace-nowrap w-36'>
                                Géneros
                            </label>
                            <Creatable
                                id='genres'
                                isMulti
                                options={genres}
                                value={selectedGenres}
                                onChange={(v) => setSelectedGenres(v)}
                                className='w-full relative z-50'
                            />
                        </div>
                        <Input id='titulo' value={serie.titulo} label='Título' labelClassName='w-36' disabled/>
                        <Input id='url' value={serie.url} label='Url' labelClassName='w-36' disabled/>
                        <div className='flex items-center gap-2 font-montserrat text-lg'>
                            <label htmlFor='estado' className='font-semibold text-red-1 whitespace-nowrap w-36'>
                                Estado
                            </label>
                            <Select
                                id='estado'
                                options={stateOptions}
                                value={{value: serie.estado, label: serie.estado}}
                                onChange={(v) => setSerie({...serie, estado: v.value})}
                                className='w-full relative z-40'
                            />
                        </div>
                        <div className='md:col-span-2'>
                            <label htmlFor='descripcion' className='font-semibold text-red-1 text-lg'>
                                Descripción
                            </label>
                            <RichTextArea value={serie.descripcion} onChange={(v) => setSerie({...serie, descripcion: v})}/>
                        </div>
                    </div>
                    <hr className='my-5 p-0'/>
                    <div>
                        <h2 className='font-semibold text-xl pb-1 mb-2'>Media</h2>
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                            <PosterMedia source={serie} type='serie' refresh={refresh}/>
                            <BackdropMedia source={serie} type='serie' refresh={refresh}/>
                        </div>
                    </div>
                    <div>
                        <h2 className='font-semibold text-xl pb-1 mb-2'>Episodios</h2>
                        <div className='flex flex-wrap items-center gap-3 w-full'>
                            {serie.capitulos.map((episode, index) => {
                                return index === episodeClicked ? (
                                    <Episode data={episode}/>
                                ) : (
                                    <button
                                        key={index}
                                        onClick={() => setEpisodeClicked(index)}
                                        className='flex items-center gap-5 p-2 rounded-lg bg-red-2 bg-opacity-5 hover:ring-1 transition-all font-semibold w-full'
                                    >
                                        <div>
                                            <FontAwesomeIcon icon={faVideo}/>
                                        </div>
                                        <div className='flex'>
                                            <span className='font-semibold text-red-1'>Temporada:&nbsp;</span>
                                            <span>{episode.temporada}</span>
                                        </div>
                                        <div className='flex'>
                                            <span className='font-semibold text-red-1'>Episodio:&nbsp;</span>
                                            <span>{episode.numero}</span>
                                        </div>
                                        <div className='flex max-w-[40vw] whitespace-nowrap overflow-x-hidden'>
                                            <span className='font-semibold text-red-1'>Url:&nbsp;</span>
                                            <span className='text-ellipsis overflow-hidden max-w-full'>{episode.url}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            ) : (
                <h1 className='font-semibold text-xl'>
                    <span className='loader2'></span>
                </h1>
            )}
        </div>
    );
};

export default AdminSerie;
